import React, { useState, useEffect } from 'react';
import { XMarkIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Photo, UpdatePhotoDto } from '../../api/photos';
import { BrandModal, BrandModalBody, BrandModalFooter, BrandModalHeader } from '../ui/BrandModal';

interface EditPhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UpdatePhotoDto) => Promise<void>;
  photo: Photo | null;
  onEditImage?: () => void;
}

const EditPhotoModal: React.FC<EditPhotoModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  photo,
  onEditImage,
}) => {
  const [formData, setFormData] = useState({
    caption: '',
    birdId: '',
    speciesId: '',
    tags: [] as string[],
    isAvatar: false,
  });

  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (photo) {
      setFormData({
        caption: photo.caption || '',
        birdId: photo.birdId || '',
        speciesId: photo.speciesId || '',
        tags: photo.tags || [],
        isAvatar: photo.isAvatar,
      });
    }
  }, [photo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error updating photo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleClose = () => {
    setTagInput('');
    onClose();
  };

  if (!photo) return null;

  return (
    <BrandModal open={isOpen} onClose={handleClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader title="Edit Photo" onClose={handleClose} />
      <form id="edit-photo-form" onSubmit={handleSubmit}>
        <BrandModalBody>
              <div className="space-y-4">
                {onEditImage && (
                  <div className="pb-2 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={onEditImage}
                      className="inline-flex items-center gap-1 text-sm text-cyan-600 hover:text-cyan-800"
                    >
                      Edit image (crop, scale, rotate, brightness)
                      <ChevronRightIcon className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                )}
                {/* Caption */}
                <div>
                  <label htmlFor="caption" className="block text-sm font-medium text-slate-700">
                    Caption
                  </label>
                  <input
                    type="text"
                    id="caption"
                    value={formData.caption}
                    onChange={(e) => setFormData(prev => ({ ...prev, caption: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    placeholder="Enter photo caption"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Bird ID */}
                  <div>
                    <label htmlFor="birdId" className="block text-sm font-medium text-slate-700">
                      Bird ID (Optional)
                    </label>
                    <input
                      type="text"
                      id="birdId"
                      value={formData.birdId}
                      onChange={(e) => setFormData(prev => ({ ...prev, birdId: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      placeholder="Associate with bird"
                    />
                  </div>

                  {/* Species ID */}
                  <div>
                    <label htmlFor="speciesId" className="block text-sm font-medium text-slate-700">
                      Species ID (Optional)
                    </label>
                    <input
                      type="text"
                      id="speciesId"
                      value={formData.speciesId}
                      onChange={(e) => setFormData(prev => ({ ...prev, speciesId: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      placeholder="Associate with species"
                    />
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label htmlFor="tags" className="block text-sm font-medium text-slate-700">
                    Tags
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={handleTagInputKeyPress}
                      className="flex-1 min-w-0 block w-full border-gray-300 rounded-none rounded-l-md focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      placeholder="Add tag and press Enter"
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-slate-50/80 text-slate-500 text-sm"
                    >
                      Add
                    </button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="flex-shrink-0 ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-indigo-400 hover:bg-indigo-200 hover:text-cyan-500 focus:outline-none focus:bg-cyan-500 focus:text-white"
                          >
                            <XMarkIcon className="h-2 w-2" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Avatar checkbox */}
                <div className="flex items-center">
                  <input
                    id="isAvatar"
                    type="checkbox"
                    checked={formData.isAvatar}
                    onChange={(e) => setFormData(prev => ({ ...prev, isAvatar: e.target.checked }))}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isAvatar" className="ml-2 block text-sm text-slate-900">
                    Set as avatar
                  </label>
                </div>
              </div>
        </BrandModalBody>

        <BrandModalFooter>
          <button type="button" onClick={handleClose} className="btn btn-secondary text-sm">
            Cancel
          </button>
          <button
            type="submit"
            form="edit-photo-form"
            disabled={loading}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {loading ? 'Updating...' : 'Update Photo'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default EditPhotoModal; 